import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { getDistanceFromKm, getDistanceFromM } from '@/app/common/measures/getters.ts';
import type { ProductGeo } from '@/domainLogic/types/typesProduct.ts';
import { DESTINATION_TYPE, type SearchInfoDestination } from '@/app/search/types.ts';
import { isGeoCity, isGeoRegion, isGeoTag, isSinglePlaceName } from '@/app/components/search/Utilities.ts';

// When the distance does not fit the card on one line it wraps; a no-break space keeps the amount and
// its unit ("1,84 km") together.
const keepAmountTogether = (formatted = '') => formatted.replace(' ', '\u00A0');

const getLocationRaw = (dealCard: DealCard, localization: Localization, t: TranslationFunction): string => {
  const geo = dealCard?.product?.geo;
  const cityDisplayName = geo?.cityDisplayName;
  const cityName = geo?.cityName;
  const isCity = cityDisplayName === cityName;
  const distance = geo?.distanceKm ? keepAmountTogether(getDistanceFromKm(geo.distanceKm, localization).formatted) : '';

  if (geo?.distanceUsp?.nearestBeach?.distanceM) {
    const beachDistance = keepAmountTogether(
      getDistanceFromM(geo.distanceUsp.nearestBeach.distanceM, localization).formatted
    );
    return t('vacation.oss.cards.location_from_beach', { city: cityDisplayName, amount: beachDistance });
  }

  if (geo?.distanceUsp?.nearestSkiLift?.distanceM) {
    const skiDistance = keepAmountTogether(
      getDistanceFromM(geo.distanceUsp.nearestSkiLift.distanceM, localization).formatted
    );
    return t('vacation.oss.cards.location_from_ski', { city: cityDisplayName, amount: skiDistance });
  }

  if (isCity) {
    return t('vacation.oss.cards.location_from_centre', { city: cityDisplayName, amount: distance });
  }

  return t('vacation.oss.cards.location_from_city', { region: cityDisplayName, amount: distance, city: cityName });
};

// The translations start with the hotel's city and a comma ("__city__, __amount__ from beach"). The city
// can hold a comma of its own ("Cala d'Or, Majorca"), so the split goes after it, not at its first comma.
export const getLocationParts = (
  dealCard: DealCard,
  localization: Localization,
  t: TranslationFunction
): { city: string; distance: string } => {
  const raw = getLocationRaw(dealCard, localization, t);
  const cityDisplayName = dealCard?.product?.geo?.cityDisplayName || '';
  const commaIdx = raw.indexOf(',', raw.startsWith(cityDisplayName) ? cityDisplayName.length : 0);
  if (commaIdx === -1) return { city: raw, distance: '' };
  return { city: raw.slice(0, commaIdx).trim(), distance: raw.slice(commaIdx + 1).trim() };
};

// Search destinations that name an area the hotels are in. An airport is described with its code
// ("Tenerife (TFS)"), a station is not where the hotel is, anywhere or a holiday type ("Beach") is not a
// place, and a country is not shown.
const PLACE_DESTINATION_TYPES = [DESTINATION_TYPE.CITY, DESTINATION_TYPE.TAG, DESTINATION_TYPE.GEO];

// Of the geo destinations, the areas: a city, a region or a tag. A point of interest (POI-1, Cerchia dei
// Navigli) or a hotel is what the hotels are near, not where they are; a country is not shown.
const isAreaDestination = ({ destinationType, id, description }: SearchInfoDestination): boolean =>
  isSinglePlaceName(description) &&
  !!destinationType &&
  PLACE_DESTINATION_TYPES.includes(destinationType) &&
  (destinationType !== DESTINATION_TYPE.GEO || isGeoCity(id) || isGeoRegion(id) || isGeoTag(id));

// Whether the hotel is in the searched city itself. Told by id: the search names the city in the page's
// language and memsearch in English (Zürich, Zurich). The name still tells a tag that is a city
// (TAG_NEW_YORK), which no hotel id matches.
const isInSearchedCity = (geo: ProductGeo | undefined, { id, description }: SearchInfoDestination): boolean =>
  id === geo?.geoDBId || id === String(geo?.internalIdCity) || description === geo?.cityDisplayName;

// What completes the hotel's city: its region (Costa Adeje · Tenerife), whatever the search, or else the
// searched area when the hotel is outside its city (Burlingame · San Francisco). Otherwise the city
// stands alone: a hotel in the searched city itself, a city that already names its area ("Cala d'Or,
// Majorca", "Agia Pelagia - Crete"), or a search that names no area (multi-destination, point of
// interest, airport, station, country, a marketing name like "Mexico - Cancun, Riviera Maya"). Not
// geo.destinationName: that is the city of the package flight's arrival airport, which labels a San
// Francisco hotel "San Jose".
export const getLocationArea = (
  dealCard: DealCard,
  destination: SearchInfoDestination | undefined
): string | undefined => {
  const geo = dealCard?.product?.geo;
  if (geo?.cityDisplayName && !isSinglePlaceName(geo.cityDisplayName)) return undefined;
  const searchedArea =
    destination?.description && isAreaDestination(destination) && !isInSearchedCity(geo, destination)
      ? destination.description
      : undefined;
  const area = geo?.regionName || searchedArea;
  return area && area !== geo?.cityDisplayName ? area : undefined;
};
